import { Link, useNavigate, useParams } from "react-router-dom";
import { ReportButton } from "@/components/moderation/ReportButton";
import { useQuery } from "@tanstack/react-query";
import { Copy, FileJson, Globe2, Hammer, Link2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, api, assetUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KIND_LABEL, zoneList } from "@/lib/decks";
import { PageLoader } from "@/components/common/PageLoader";
import { EmptyState, PageHeader } from "@/components/common/PageParts";
import { VisibilityBadge } from "@/components/common/Badges";
import { DeckStats } from "@/components/decks/DeckStats";

function ZoneGallery({ zone, entries }) {
  if (!entries.length) return null;
  return (
    <section className="mb-10" data-testid={`deck-view-zone-${zone.key}`}>
      <h2 className="mb-4 font-heading text-lg text-amber-200">{zone.label} <span className="font-mono text-sm text-slate-500">({entries.reduce((s, e) => s + e.quantity, 0)})</span></h2>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 xl:grid-cols-6">
        {entries.map((e) => {
          const card = e.card;
          const inner = (
            <div className="card-frame aspect-[63/88]">
              {card?.image_url ? <img src={assetUrl(card.image_url)} alt={card.name} className="h-full w-full object-cover" loading="lazy" /> : <div className="p-2 text-xs text-red-300">Unavailable</div>}
              <span className="absolute bottom-1.5 right-1.5 rounded bg-black/80 px-1.5 font-mono text-xs text-aether-300">{e.quantity}×</span>
            </div>
          );
          return card?.source === "custom" ? <Link key={`${e.source}-${e.ref_id}`} to={`/cards/${card.id}`}>{inner}</Link>
            : <a key={`${e.source}-${e.ref_id}`} href={card?.scryfall_uri} target="_blank" rel="noreferrer">{inner}</a>;
        })}
      </div>
    </section>
  );
}

export default function DeckView() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: deck, isLoading, isError } = useQuery({ queryKey: ["decks", "detail", deckId], queryFn: () => api.get(`/decks/${deckId}`).then((r) => r.data), retry: false });
  if (isLoading) return <PageLoader />;
  if (isError) return <div className="container-x py-24"><EmptyState title="Deck not found" testid="deck-not-found">It may be private or still a draft.</EmptyState></div>;
  const mine = user && user.user_id === deck.owner_id;
  const duplicate = async () => {
    try {
      const { data } = await api.post(`/decks/${deckId}/duplicate`);
      toast.success("Deck copied to your workshop");
      navigate(`/decks/${data.deck_id}/build`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const importable = deck.published && deck.visibility !== "private";
  const copyTtsLink = async () => {
    if (!importable) return toast.error("Only published public or unlisted decks can be imported into Tabletop Simulator");
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/decks/${deckId}`);
      toast.success("Deck link copied. Paste it into the Deck Import Terminal in TTS");
    } catch {
      toast.error("Copy failed. Copy the address bar link instead");
    }
  };
  return (
    <div className="container-x py-12" data-testid="deck-view-page">
      <PageHeader eyebrow={`${deck.format_label} · ${KIND_LABEL[deck.kind]}${deck.published ? "" : " · Draft"}`} title={deck.name} testid="deck-view-header"
        description={deck.description || (deck.owner ? `Built by @${deck.owner.username}` : "")}
        actions={<>
          <VisibilityBadge visibility={deck.visibility} />
          {mine && <Link to={`/decks/${deckId}/build`} className="btn-gold" data-testid="deck-edit-button"><Hammer className="h-4 w-4" />Edit in builder</Link>}
          {user && <button className="btn-outline" onClick={duplicate} data-testid="deck-duplicate-button"><Copy className="h-4 w-4" />Duplicate</button>}
          <button className="btn-outline" onClick={copyTtsLink} title={importable ? "Copy link for the TTS Deck Import Terminal" : "Publish as public or unlisted to import in TTS"} data-testid="deck-copy-tts-link"><Link2 className="h-4 w-4" />Copy TTS import link</button>
          {user && deck.entries.some((e) => ["planar", "scheme", "vanguard"].includes(e.zone)) && <Link to={`/companion?deck=${deckId}`} className="btn-outline" data-testid="deck-launch-companion"><Globe2 className="h-4 w-4" />Launch companion</Link>}
          <a href={`${BACKEND_URL}/api/v1/decks/${deckId}/tts`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="deck-tts-link"><FileJson className="h-4 w-4" />TTS manifest (JSON)</a>
          {!mine && <ReportButton targetType="deck" targetId={deckId} label="Report deck" />}
        </>} />
      <p className="-mt-6 mb-8 font-mono text-xs text-slate-500" data-testid="deck-view-id">Permanent deck ID: {deck.deck_id}</p>
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-9">
          {deck.entries.length ? zoneList(deck.available_zones, deck.custom_zones).map((z) => <ZoneGallery key={z.key} zone={z} entries={deck.entries.filter((e) => e.zone === z.key)} />)
            : <EmptyState title="This deck is empty" testid="deck-view-empty" />}
        </div>
        <div className="lg:col-span-3"><DeckStats stats={deck.stats} warnings={deck.warnings} /></div>
      </div>
    </div>
  );
}
